import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { User, ChevronDown, Sun, Moon, Monitor, Globe } from 'lucide-react';
import { useThemeStore } from '../../stores/themeStore';
import 'flag-icons/css/flag-icons.min.css';

interface UserMenuProps {
  username?: string;
  onProfileClick: () => void;
  onLogout?: () => void;
  onLogin?: () => void;
  onRegister?: () => void;
  isAuthenticated: boolean;
}

export default function UserMenu({ username, onProfileClick, onLogout, onLogin, onRegister, isAuthenticated }: UserMenuProps) {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, setTheme } = useThemeStore();

  const handleLanguageChange = (lang: 'en' | 'de') => {
    i18n.changeLanguage(lang);
  };

  return (
    <div className="relative">
      <motion.button
        onClick={() => setMenuOpen(!menuOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-surface hover:bg-surface-elevated transition-colors border border-border"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="User menu"
        aria-expanded={menuOpen}
      >
        <User className="w-5 h-5" />
        {isAuthenticated && username && <span className="hidden sm:inline">{username}</span>}
        <ChevronDown className={`w-4 h-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
      </motion.button>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="absolute top-full right-0 mt-2 bg-surface rounded-lg border border-border shadow-xl z-50 min-w-max"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {/* Theme Section */}
            <div className="px-4 py-2 border-b border-border">
              <p className="text-sm font-semibold text-text-secondary mb-2 flex items-center gap-2">
                <Sun className="w-4 h-4" />
                {t('ui.theme')}
              </p>
              <div className="space-y-1">
                <motion.button
                  onClick={() => { setTheme('light'); setMenuOpen(false); }}
                  className={`w-full px-3 py-1 rounded text-sm text-left flex items-center gap-2 transition-colors ${
                    theme === 'light' 
                      ? 'bg-primary/20 text-primary' 
                      : 'hover:bg-surface-elevated text-text-primary'
                  }`}
                  whileHover={{ x: 4 }}
                >
                  <Sun className="w-4 h-4" />
                  {t('ui.lightMode')}
                </motion.button>
                <motion.button
                  onClick={() => { setTheme('dark'); setMenuOpen(false); }}
                  className={`w-full px-3 py-1 rounded text-sm text-left flex items-center gap-2 transition-colors ${
                    theme === 'dark' 
                      ? 'bg-primary/20 text-primary' 
                      : 'hover:bg-surface-elevated text-text-primary'
                  }`}
                  whileHover={{ x: 4 }}
                >
                  <Moon className="w-4 h-4" />
                  {t('ui.darkMode')}
                </motion.button>
                <motion.button
                  onClick={() => { setTheme('system'); setMenuOpen(false); }}
                  className={`w-full px-3 py-1 rounded text-sm text-left flex items-center gap-2 transition-colors ${
                    theme === 'system' 
                      ? 'bg-primary/20 text-primary' 
                      : 'hover:bg-surface-elevated text-text-primary'
                  }`}
                  whileHover={{ x: 4 }}
                >
                  <Monitor className="w-4 h-4" />
                  {t('ui.systemMode')}
                </motion.button>
              </div>
            </div>

            {/* Language Section */}
            <div className="px-4 py-2 border-b border-border">
              <p className="text-sm font-semibold text-text-secondary mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                {t('ui.language')}
              </p>
              <div className="space-y-1">
                <motion.button
                  onClick={() => { handleLanguageChange('de'); setMenuOpen(false); }}
                  className={`w-full px-3 py-1 rounded text-sm text-left flex items-center gap-2 transition-colors ${
                    i18n.language === 'de' 
                      ? 'bg-primary/20 text-primary' 
                      : 'hover:bg-surface-elevated text-text-primary'
                  }`}
                  whileHover={{ x: 4 }}
                >
                  <span className="fi fi-de h-4 w-6" />
                  Deutsch
                </motion.button>
                <motion.button
                  onClick={() => { handleLanguageChange('en'); setMenuOpen(false); }}
                  className={`w-full px-3 py-1 rounded text-sm text-left flex items-center gap-2 transition-colors ${
                    i18n.language === 'en' 
                      ? 'bg-primary/20 text-primary' 
                      : 'hover:bg-surface-elevated text-text-primary'
                  }`}
                  whileHover={{ x: 4 }}
                >
                  <span className="fi fi-gb h-4 w-6" />
                  English
                </motion.button>
              </div>
            </div>

            {/* Profile & Logout or Login/Register */}
            {isAuthenticated ? (
              <>
                <motion.button
                  onClick={() => { onProfileClick(); setMenuOpen(false); }}
                  className="w-full px-4 py-2 text-left hover:bg-surface-elevated text-text-primary transition-colors flex items-center gap-3 border-t border-border"
                  whileHover={{ x: 4 }}
                >
                  <User className="w-4 h-4" />
                  {t('ui.profileSettings')}
                </motion.button>
                {onLogout && (
                  <motion.button
                    onClick={() => { onLogout(); setMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-danger/10 text-danger transition-colors flex items-center gap-3 rounded-b-lg"
                    whileHover={{ x: 4 }}
                  >
                    {t('auth.logout')}
                  </motion.button>
                )}
              </>
            ) : (
              <>
                {onLogin && (
                  <motion.button
                    onClick={() => { onLogin(); setMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-surface-elevated text-text-primary transition-colors flex items-center gap-3 border-t border-border"
                    whileHover={{ x: 4 }}
                  >
                    <User className="w-4 h-4" />
                    {t('auth.login') || 'Anmelden'}
                  </motion.button>
                )}
                {onRegister && (
                  <motion.button
                    onClick={() => { onRegister(); setMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-primary/10 text-primary transition-colors flex items-center gap-3 rounded-b-lg"
                    whileHover={{ x: 4 }}
                  >
                    {t('auth.register') || 'Registrieren'}
                  </motion.button>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
